import { getDefaultCompanyInfo } from '../../../services/companyInfoService';

// Hook pour gérer la conversion HTML et Markdown
export const useHTMLConversion = () => {
  
  // Convertir Markdown en HTML avec un rendu riche et professionnel
  const convertMarkdownToHTML = (markdown) => {
    if (!markdown || markdown.trim() === '') {
      return '<p class="empty-content">Aucun contenu à afficher</p>';
    }
    
    let html = markdown;
    
    // Titres avec classes CSS
    html = html.replace(/^# (.*?)$/gm, '<h1 class="document-title">$1</h1>');
    html = html.replace(/^## (.*?)$/gm, '<h2 class="document-section">$1</h2>');
    html = html.replace(/^### (.*?)$/gm, '<h3 class="document-subsection">$1</h3>');
    html = html.replace(/^#### (.*?)$/gm, '<h4 class="document-subtitle">$1</h4>');
    
    // Gras et italique
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="text-bold">$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em class="text-italic">$1</em>');
    
    // Listes à puces avec gestion multi-niveaux
    html = html.replace(/^- (.*?)$/gm, '<li class="list-item">$1</li>');
    html = html.replace(/(<li class="list-item">.*<\/li>)/gs, '<ul class="bullet-list">$1</ul>');
    
    // Listes numérotées
    html = html.replace(/^\d+\. (.*?)$/gm, '<li class="list-item">$1</li>');
    html = html.replace(/(<li class="list-item">.*<\/li>)/gs, '<ol class="numbered-list">$1</ol>');
    
    // Paragraphes avec gestion des sauts de ligne
    html = html.replace(/^(?!<[hluo])([^\n].*?)$/gm, '<p class="document-paragraph">$1</p>');
    
    // Gestion des sauts de ligne dans les paragraphes
    html = html.replace(/\n\n/g, '</p><p class="document-paragraph">');
    
    // Encadrés et sections importantes
    html = html.replace(/^> (.*?)$/gm, '<div class="callout-box">$1</div>');
    
    // Liens
    html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="document-link">$1</a>');
    
    // Séparateurs
    html = html.replace(/^---$/gm, '<hr class="document-separator">');
    
    // Citations
    html = html.replace(/^> (.*?)$/gm, '<blockquote class="document-quote">$1</blockquote>');
    
    // Code inline
    html = html.replace(/`(.*?)`/g, '<code class="inline-code">$1</code>');
    
    // Nettoyer les paragraphes vides
    html = html.replace(/<p class="document-paragraph"><\/p>/g, '');
    
    // Ajouter un wrapper pour le contenu
    html = `<div class="document-content-rich">${html}</div>`;
    
    return html;
  };

  // Convertir le contenu simple en HTML pour l'affichage (compatibilité)
  const convertToHTML = (content) => {
    return convertMarkdownToHTML(content);
  };

  // Convertir le contenu édité en HTML complet avec header/footer (version synchrone pour prévisualisation)
  const convertToFullHTMLPreview = (content, selectedDocument) => {
    const currentDate = new Date().toLocaleDateString('fr-FR');
    
    // Utiliser les informations par défaut pour la prévisualisation avec logo
    const companyInfo = {
      ...getDefaultCompanyInfo(),
      logoUrl: '/logo.png' // Logo par défaut pour la prévisualisation
    };
    
    // Style CSS basé sur livret.html
    const cssStyle = `
      :root{
        --brand:#125E8A;         /* bleu principal */
        --brand-alt:#0B3E59;     /* bleu foncé */
        --accent:#27A2DB;        /* bleu clair */
        --ink:#1f2937;           /* texte */
        --muted:#6b7280;         /* texte secondaire */
        --line:#e5e7eb;          /* bordures */
        --bg:#ffffff;            /* fond */
        --chip:#f3f4f6;          /* pastilles */
      }

      /* Print button styles */
      .print-btn {
        display: inline-block;
        background: var(--brand);
        color: white;
        padding: 10px 20px;
        border-radius: 6px;
        text-decoration: none;
        font-weight: 600;
        margin: 20px 0;
        cursor: pointer;
        border: none;
        font-family: inherit;
      }

      .print-btn:hover {
        background: var(--brand-alt);
      }

      .print-btn:active {
        transform: translateY(1px);
      }

      /* Reset léger + typographie */
      *{box-sizing:border-box}
      html,body{margin:0;padding:0;background:var(--bg);color:var(--ink);font-family:Inter,system-ui,Segoe UI,Roboto,Helvetica,Arial,sans-serif; line-height:1.55}
      h1,h2,h3{line-height:1.25;margin:0 0 .6rem}
      h1{font-size:clamp(1.8rem,2.5vw,2.4rem);font-weight:800;color:var(--brand)}
      h2{font-size:clamp(1.3rem,2vw,1.6rem);font-weight:700;color:var(--brand-alt);margin-top:2.2rem}
      h3{font-size:1.05rem;font-weight:700;color:var(--ink);margin-top:1.2rem}
      p{margin:.35rem 0 .75rem}
      a{color:var(--brand);text-decoration:none}
      a:hover{text-decoration:underline}
      ul,ol{margin:.5rem 0 .9rem 1.1rem}
      .wrap{max-width:980px;margin:0 auto;padding:28px 20px 64px}

      /* En-tête */
      header{
        display:flex;gap:24px;align-items:center;padding:24px 0 20px;border-bottom:2px solid var(--brand);margin-bottom:24px
      }
      .logo{
        width:140px;height:auto;flex:0 0 auto;border-radius:16px;box-shadow:0 4px 20px rgba(0,0,0,.08);background:#fff;padding:8px
      }
      .head-title{display:flex;flex-direction:column;flex:1}
      .brand{font-size:1.8rem;font-weight:800;letter-spacing:.3px;color:var(--brand);margin-bottom:4px}
      .subtitle{color:var(--muted);font-size:1.05rem;font-weight:500}

      /* Sommaire */
      .toc{
        display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:10px;margin:14px 0 24px
      }
      .toc a{
        display:block;padding:10px 12px;border:1px solid var(--line);border-radius:10px;background:#fff
      }

      /* Cartes de section */
      section{padding:12px 0}
      .card{
        border:1px solid var(--line);border-radius:14px;padding:18px 16px;background:#fff;box-shadow:0 2px 12px rgba(0,0,0,.03)
      }
      .meta{display:flex;flex-wrap:wrap;gap:10px;margin:.5rem 0 0}
      .chip{background:var(--chip);color:var(--muted);padding:6px 10px;border-radius:999px;font-size:.9rem}

      /* Tableaux */
      .table{
        width:100%;border-collapse:separate;border-spacing:0;border:1px solid var(--line);border-radius:12px;overflow:hidden;margin:.6rem 0 1rem
      }
      .table th,.table td{padding:10px 12px;border-bottom:1px solid var(--line);vertical-align:top}
      .table thead th{background:#f9fafb;font-weight:700;text-align:left}
      .table tr:last-child td{border-bottom:none}
      .note{font-size:.92rem;color:var(--muted)}

      /* Encarts */
      .callout{
        border-left:4px solid var(--accent);background:#f0f9ff;padding:12px 14px;border-radius:8px;margin:.7rem 0 1rem
      }

      /* Footer */
      footer{margin-top:36px;padding-top:18px;border-top:1px solid var(--line);color:var(--muted);font-size:.92rem}

      /* Impression */
      @media print{
        header{border:none;margin:0 0 6px}
        .wrap{padding:0}
        a{color:inherit;text-decoration:none}
        .card{box-shadow:none;page-break-inside:avoid}
        .table{page-break-inside:auto}
        .toc{display:block}
        .toc a{border:none;padding:0}
      }
    `;

    // Header avec logo et informations de la société
    const headerHTML = `
      <header>
        ${companyInfo.logoUrl ? `<img class="logo" alt="Logo ${companyInfo.denominationSociale}" src="${companyInfo.logoUrl}"/>` : ''}
        <div class="head-title">
          <h1>${selectedDocument?.documentName || 'Document'} – ${companyInfo.denominationSociale}</h1>
          <div class="subtitle">Document officiel — version du ${currentDate}</div>
        </div>
      </header>
    `;

    // Footer avec informations de contact
    const footerHTML = `
      <footer>
        © ${companyInfo.denominationSociale} — ${companyInfo.siegeSocial} — 
        ${companyInfo.telephone ? `Tél : ${companyInfo.telephone}` : ''} — 
        ${companyInfo.email ? `Email : ${companyInfo.email}` : ''}
      </footer>
    `;

    return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${selectedDocument?.documentName || 'Document'} – ${companyInfo.denominationSociale}</title>
  <meta name="description" content="${selectedDocument?.description || 'Document générique'}" />
  <style>${cssStyle}</style>
</head>
<body>
  <div class="wrap">
    ${headerHTML}
    ${content}
    ${footerHTML}
  </div>
</body>
</html>`;
  };

  return {
    convertMarkdownToHTML,
    convertToHTML,
    convertToFullHTMLPreview
  };
};
